import { Group, InboxOutlined, Storefront } from '@mui/icons-material';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { ReactNode } from 'react';
import {
	IndicacaoGrupoIndicado,
	IndicacaoLojistaIndicado,
} from '../../../services/indicacao';
import { formatDate } from '../indicacao.utils';
import { DataGridColumn, DataGridView } from './DataGridView';
import { EmptyState } from './EmptyState';
import { SectionHeader } from './SectionHeader';

interface IndicadosGridProps {
	lojistas: IndicacaoLojistaIndicado[];
	grupos: IndicacaoGrupoIndicado[];
	periodoFiltrado: boolean;
}

const SituacaoChip = ({ ativo }: { ativo: string }) => {
	const estaAtivo = ativo === '1';
	return (
		<Chip
			size="small"
			label={estaAtivo ? 'Ativo' : 'Inativo'}
			sx={{
				fontWeight: 700,
				color: estaAtivo ? 'rgb(5, 150, 105)' : 'rgb(100, 116, 139)',
				bgcolor: estaAtivo ? 'rgba(5, 150, 105, 0.12)' : '#f1f5f9',
			}}
		/>
	);
};

const cartao = (titulo: string, data: string, situacao: ReactNode) => (
	<Box
		sx={{
			p: 1.75,
			borderRadius: 2.5,
			border: '1px solid #f1f5f9',
			display: 'flex',
			flexDirection: 'column',
			gap: 1.25,
		}}
	>
		<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
			<Typography fontWeight={800} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
				{titulo || '—'}
			</Typography>
			{situacao}
		</Box>
		<Typography fontSize="0.8rem" color="text.secondary">
			Indicado em {formatDate(data)}
		</Typography>
	</Box>
);

const colunasLojista: DataGridColumn<IndicacaoLojistaIndicado>[] = [
	{
		id: 'revenda',
		label: 'Revenda',
		render: (item) => item.revenda || '—',
	},
	{
		id: 'situacao',
		label: 'Situação',
		render: (item) => <SituacaoChip ativo={item.ativo} />,
	},
	{
		id: 'data',
		label: 'Data da indicação',
		render: (item) => formatDate(item.data_cadastro),
	},
];

const colunasGrupo: DataGridColumn<IndicacaoGrupoIndicado>[] = [
	{
		id: 'grupo',
		label: 'Grupo',
		render: (item) => item.desc_grupo || '—',
	},
	{
		id: 'situacao',
		label: 'Situação',
		render: (item) => <SituacaoChip ativo={item.ativo} />,
	},
	{
		id: 'data',
		label: 'Data da indicação',
		render: (item) => formatDate(item.data_cadastro),
	},
];

export function IndicadosGrid({ lojistas, grupos, periodoFiltrado }: IndicadosGridProps) {
	const vazioLojistas = periodoFiltrado
		? 'Nenhum lojista indicado neste período.'
		: 'Nenhum lojista indicado.';
	const vazioGrupos = periodoFiltrado
		? 'Nenhum grupo indicado neste período.'
		: 'Nenhum grupo indicado.';

	return (
		<Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
			<Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #f1f5f9' }}>
				<Box mb={2}>
					<SectionHeader
						icon={<Storefront />}
						titulo="Lojistas indicados"
						descricao="Revendas cadastradas pela indicação."
					/>
				</Box>
				<DataGridView
					columns={colunasLojista}
					rows={lojistas}
					getRowId={(item) => item.id}
					empty={<EmptyState icon={<InboxOutlined />} texto={vazioLojistas} />}
					renderMobileCard={(item) =>
						cartao(item.revenda, item.data_cadastro, <SituacaoChip ativo={item.ativo} />)
					}
				/>
			</Paper>
			<Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #f1f5f9' }}>
				<Box mb={2}>
					<SectionHeader
						icon={<Group />}
						titulo="Grupos indicados"
						descricao="Grupos de concessionária vinculados à indicação."
					/>
				</Box>
				<DataGridView
					columns={colunasGrupo}
					rows={grupos}
					getRowId={(item) => item.id}
					empty={<EmptyState icon={<InboxOutlined />} texto={vazioGrupos} />}
					renderMobileCard={(item) =>
						cartao(item.desc_grupo, item.data_cadastro, <SituacaoChip ativo={item.ativo} />)
					}
				/>
			</Paper>
		</Box>
	);
}
